import { cn } from '@/lib/utils';

interface DiagramProps {
  /** Тип диаграммы */
  type:
    | 'three-trees'
    | 'git-flow'
    | 'commit-structure'
    | 'branch-merge'
    | 'staging-flow'
    | 'reset-modes'
    | 'remote-workflow'
    | 'rebase-vs-merge'
    | 'git-init-process'
    | 'git-add-process'
    | 'git-commit-process'
    | 'branch-operations'
    | 'stash-workflow'
    | 'cherry-pick-flow'
    | 'conflict-resolution'
    | 'head-movement';
  /** Дополнительные CSS классы */
  className?: string;
}

/**
 * Компонент для отображения SVG-диаграмм в уроках
 */
export function Diagram({ type, className }: DiagramProps) {
  const renderDiagram = () => {
    switch (type) {
      case 'three-trees':
        return <ThreeTreesDiagram />;
      case 'git-flow':
        return <GitFlowDiagram />;
      case 'commit-structure':
        return <CommitStructureDiagram />;
      case 'branch-merge':
        return <BranchMergeDiagram />;
      case 'staging-flow':
        return <StagingFlowDiagram />;
      case 'reset-modes':
        return <ResetModesDiagram />;
      case 'remote-workflow':
        return <RemoteWorkflowDiagram />;
      case 'rebase-vs-merge':
        return <RebaseVsMergeDiagram />;
      case 'git-init-process':
        return <GitInitProcessDiagram />;
      case 'git-add-process':
        return <GitAddProcessDiagram />;
      case 'git-commit-process':
        return <GitCommitProcessDiagram />;
      case 'branch-operations':
        return <BranchOperationsDiagram />;
      case 'stash-workflow':
        return <StashWorkflowDiagram />;
      case 'cherry-pick-flow':
        return <CherryPickFlowDiagram />;
      case 'conflict-resolution':
        return <ConflictResolutionDiagram />;
      case 'head-movement':
        return <HeadMovementDiagram />;
      default:
        return null;
    }
  };

  return (
    <div className={cn('my-6 flex justify-center', className)}>
      <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-4 overflow-x-auto max-w-full">
        {renderDiagram()}
      </div>
    </div>
  );
}

/**
 * Диаграмма трёх деревьев Git
 */
function ThreeTreesDiagram() {
  return (
    <svg width="500" height="200" viewBox="0 0 500 200" className="min-w-[400px]">
      {/* Working Directory */}
      <rect
        x="10"
        y="50"
        width="140"
        height="100"
        rx="8"
        fill="#06b6d4"
        fillOpacity="0.2"
        stroke="#06b6d4"
        strokeWidth="2"
      />
      <text x="80" y="30" textAnchor="middle" fill="#06b6d4" fontSize="14" fontWeight="600">
        Working Directory
      </text>
      <text x="80" y="80" textAnchor="middle" fill="#94a3b8" fontSize="12">
        Ваши файлы
      </text>
      <text x="80" y="100" textAnchor="middle" fill="#94a3b8" fontSize="12">
        на диске
      </text>
      <rect x="30" y="115" width="100" height="25" rx="4" fill="#0f172a" stroke="#475569" />
      <text x="80" y="132" textAnchor="middle" fill="#e2e8f0" fontSize="10">
        file.txt
      </text>

      {/* Arrow 1 */}
      <path d="M160 100 L190 100" stroke="#f97316" strokeWidth="2" markerEnd="url(#arrow)" />
      <text x="175" y="90" textAnchor="middle" fill="#f97316" fontSize="10">
        git add
      </text>

      {/* Staging Area */}
      <rect
        x="200"
        y="50"
        width="140"
        height="100"
        rx="8"
        fill="#8b5cf6"
        fillOpacity="0.2"
        stroke="#8b5cf6"
        strokeWidth="2"
      />
      <text x="270" y="30" textAnchor="middle" fill="#8b5cf6" fontSize="14" fontWeight="600">
        Staging Area
      </text>
      <text x="270" y="80" textAnchor="middle" fill="#94a3b8" fontSize="12">
        Изменения
      </text>
      <text x="270" y="100" textAnchor="middle" fill="#94a3b8" fontSize="12">
        готовые к коммиту
      </text>
      <rect x="220" y="115" width="100" height="25" rx="4" fill="#0f172a" stroke="#475569" />
      <text x="270" y="132" textAnchor="middle" fill="#e2e8f0" fontSize="10">
        file.txt
      </text>

      {/* Arrow 2 */}
      <path d="M350 100 L380 100" stroke="#f97316" strokeWidth="2" markerEnd="url(#arrow)" />
      <text x="365" y="90" textAnchor="middle" fill="#f97316" fontSize="10">
        git commit
      </text>

      {/* Repository */}
      <rect
        x="390"
        y="50"
        width="100"
        height="100"
        rx="8"
        fill="#f97316"
        fillOpacity="0.2"
        stroke="#f97316"
        strokeWidth="2"
      />
      <text x="440" y="30" textAnchor="middle" fill="#f97316" fontSize="14" fontWeight="600">
        Repository
      </text>
      <text x="440" y="80" textAnchor="middle" fill="#94a3b8" fontSize="12">
        История
      </text>
      <text x="440" y="100" textAnchor="middle" fill="#94a3b8" fontSize="12">
        коммитов
      </text>

      {/* Commit icon */}
      <circle cx="440" cy="125" r="12" fill="#f97316" />
      <text x="440" y="129" textAnchor="middle" fill="white" fontSize="8">
        C1
      </text>

      {/* Arrow marker definition */}
      <defs>
        <marker
          id="arrow"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L0,6 L9,3 z" fill="#f97316" />
        </marker>
      </defs>
    </svg>
  );
}

/**
 * Диаграмма Git Flow
 */
function GitFlowDiagram() {
  return (
    <svg width="550" height="280" viewBox="0 0 550 280" className="min-w-[500px]">
      {/* Main branch */}
      <line x1="50" y1="60" x2="500" y2="60" stroke="#f97316" strokeWidth="3" />
      <text x="25" y="65" fill="#f97316" fontSize="12" fontWeight="600">
        main
      </text>

      {/* Commits on main */}
      <circle cx="100" cy="60" r="8" fill="#f97316" />
      <circle cx="200" cy="60" r="8" fill="#f97316" />
      <circle cx="450" cy="60" r="8" fill="#f97316" />

      {/* Develop branch */}
      <line x1="100" y1="60" x2="100" y2="120" stroke="#8b5cf6" strokeWidth="2" />
      <line x1="100" y1="120" x2="400" y2="120" stroke="#8b5cf6" strokeWidth="3" />
      <text x="25" y="125" fill="#8b5cf6" fontSize="12" fontWeight="600">
        develop
      </text>

      {/* Commits on develop */}
      <circle cx="150" cy="120" r="6" fill="#8b5cf6" />
      <circle cx="200" cy="120" r="6" fill="#8b5cf6" />
      <circle cx="250" cy="120" r="6" fill="#8b5cf6" />
      <circle cx="350" cy="120" r="6" fill="#8b5cf6" />

      {/* Feature branch */}
      <line x1="150" y1="120" x2="150" y2="180" stroke="#06b6d4" strokeWidth="2" />
      <line x1="150" y1="180" x2="300" y2="180" stroke="#06b6d4" strokeWidth="2" />
      <line x1="300" y1="180" x2="300" y2="120" stroke="#06b6d4" strokeWidth="2" />
      <text x="25" y="185" fill="#06b6d4" fontSize="12" fontWeight="600">
        feature
      </text>

      {/* Commits on feature */}
      <circle cx="180" cy="180" r="5" fill="#06b6d4" />
      <circle cx="220" cy="180" r="5" fill="#06b6d4" />
      <circle cx="260" cy="180" r="5" fill="#06b6d4" />

      {/* Hotfix branch */}
      <line x1="200" y1="60" x2="200" y2="240" stroke="#ef4444" strokeWidth="2" />
      <line x1="200" y1="240" x2="280" y2="240" stroke="#ef4444" strokeWidth="2" />
      <line x1="280" y1="240" x2="280" y2="60" stroke="#ef4444" strokeWidth="2" />
      <text x="25" y="245" fill="#ef4444" fontSize="12" fontWeight="600">
        hotfix
      </text>

      {/* Commits on hotfix */}
      <circle cx="240" cy="240" r="5" fill="#ef4444" />

      {/* Merge arrows */}
      <text x="310" y="175" fill="#94a3b8" fontSize="10">
        merge
      </text>
      <text x="290" y="55" fill="#94a3b8" fontSize="10">
        merge
      </text>

      {/* Legend */}
      <rect x="380" y="200" width="150" height="70" rx="4" fill="#0f172a" stroke="#475569" />
      <circle cx="400" cy="220" r="5" fill="#f97316" />
      <text x="415" y="224" fill="#94a3b8" fontSize="10">
        main - production
      </text>
      <circle cx="400" cy="240" r="5" fill="#8b5cf6" />
      <text x="415" y="244" fill="#94a3b8" fontSize="10">
        develop - разработка
      </text>
      <circle cx="400" cy="260" r="5" fill="#06b6d4" />
      <text x="415" y="264" fill="#94a3b8" fontSize="10">
        feature - функции
      </text>
    </svg>
  );
}

/**
 * Структура коммита
 */
function CommitStructureDiagram() {
  return (
    <svg width="400" height="250" viewBox="0 0 400 250" className="min-w-[350px]">
      {/* Commit box */}
      <rect
        x="50"
        y="20"
        width="300"
        height="210"
        rx="8"
        fill="#0f172a"
        stroke="#475569"
        strokeWidth="2"
      />

      {/* Header */}
      <rect x="50" y="20" width="300" height="40" rx="8" fill="#f97316" fillOpacity="0.2" />
      <rect x="50" y="52" width="300" height="8" fill="#0f172a" />
      <text x="200" y="45" textAnchor="middle" fill="#f97316" fontSize="14" fontWeight="600">
        Commit
      </text>

      {/* SHA */}
      <text x="70" y="85" fill="#94a3b8" fontSize="11">
        SHA-1 Hash:
      </text>
      <text x="170" y="85" fill="#22c55e" fontSize="11" fontFamily="monospace">
        a1b2c3d4e5f6...
      </text>

      {/* Author */}
      <text x="70" y="110" fill="#94a3b8" fontSize="11">
        Author:
      </text>
      <text x="170" y="110" fill="#e2e8f0" fontSize="11">
        Ivan Petrov {'<'}ivan@example.com{'>'}
      </text>

      {/* Date */}
      <text x="70" y="135" fill="#94a3b8" fontSize="11">
        Date:
      </text>
      <text x="170" y="135" fill="#e2e8f0" fontSize="11">
        2024-01-15 14:30:00
      </text>

      {/* Message */}
      <text x="70" y="160" fill="#94a3b8" fontSize="11">
        Message:
      </text>
      <rect x="70" y="170" width="260" height="45" rx="4" fill="#1e293b" stroke="#475569" />
      <text x="85" y="190" fill="#e2e8f0" fontSize="11">
        Добавить функцию авторизации
      </text>
      <text x="85" y="205" fill="#94a3b8" fontSize="10">
        - Добавлен компонент Login
      </text>

      {/* Parent pointer */}
      <text x="70" y="225" fill="#94a3b8" fontSize="11">
        Parent:
      </text>
      <text x="170" y="225" fill="#8b5cf6" fontSize="11" fontFamily="monospace">
        f7e8d9c8b7a6...
      </text>
    </svg>
  );
}

/**
 * Диаграмма слияния веток
 */
function BranchMergeDiagram() {
  return (
    <svg width="450" height="200" viewBox="0 0 450 200" className="min-w-[400px]">
      {/* Main branch */}
      <line x1="30" y1="50" x2="420" y2="50" stroke="#f97316" strokeWidth="3" />
      <text x="30" y="30" fill="#f97316" fontSize="12" fontWeight="600">
        main
      </text>

      {/* Commits on main before branch */}
      <circle cx="80" cy="50" r="8" fill="#f97316" />
      <text x="80" y="80" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C1
      </text>

      <circle cx="150" cy="50" r="8" fill="#f97316" />
      <text x="150" y="80" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C2
      </text>

      {/* Branch point */}
      <circle cx="220" cy="50" r="8" fill="#f97316" />
      <text x="220" y="80" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C3
      </text>

      {/* Feature branch */}
      <path d="M220 50 L220 120 L350 120" stroke="#06b6d4" strokeWidth="2" fill="none" />
      <text x="355" y="125" fill="#06b6d4" fontSize="12" fontWeight="600">
        feature
      </text>

      {/* Commits on feature */}
      <circle cx="260" cy="120" r="6" fill="#06b6d4" />
      <text x="260" y="145" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C4
      </text>

      <circle cx="310" cy="120" r="6" fill="#06b6d4" />
      <text x="310" y="145" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C5
      </text>

      {/* Merge back */}
      <path d="M350 120 L350 50" stroke="#06b6d4" strokeWidth="2" strokeDasharray="4" />

      {/* Merge commit */}
      <circle cx="350" cy="50" r="10" fill="#22c55e" stroke="#16a34a" strokeWidth="2" />
      <text x="350" y="80" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C6 (merge)
      </text>

      {/* Continue main */}
      <circle cx="420" cy="50" r="8" fill="#f97316" />
      <text x="420" y="80" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C7
      </text>

      {/* Legend */}
      <rect x="30" y="160" width="200" height="30" rx="4" fill="#0f172a" stroke="#475569" />
      <circle cx="50" cy="175" r="5" fill="#f97316" />
      <text x="60" y="179" fill="#94a3b8" fontSize="9">
        main commits
      </text>
      <circle cx="130" cy="175" r="5" fill="#06b6d4" />
      <text x="140" y="179" fill="#94a3b8" fontSize="9">
        feature commits
      </text>
      <circle cx="210" cy="175" r="5" fill="#22c55e" />
      <text x="220" y="179" fill="#94a3b8" fontSize="9">
        merge
      </text>
    </svg>
  );
}

/**
 * Диаграмма потока Staging
 */
function StagingFlowDiagram() {
  return (
    <svg width="500" height="180" viewBox="0 0 500 180" className="min-w-[450px]">
      {/* Untracked */}
      <rect
        x="10"
        y="60"
        width="90"
        height="60"
        rx="6"
        fill="#ef4444"
        fillOpacity="0.2"
        stroke="#ef4444"
        strokeWidth="2"
      />
      <text x="55" y="40" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="600">
        Untracked
      </text>
      <text x="55" y="85" textAnchor="middle" fill="#94a3b8" fontSize="10">
        Новый файл
      </text>
      <text x="55" y="100" textAnchor="middle" fill="#94a3b8" fontSize="10">
        ? file.txt
      </text>

      {/* Arrow */}
      <path d="M110 90 L140 90" stroke="#f97316" strokeWidth="2" markerEnd="url(#arrow2)" />
      <text x="125" y="80" textAnchor="middle" fill="#f97316" fontSize="9">
        git add
      </text>

      {/* Staged */}
      <rect
        x="150"
        y="60"
        width="90"
        height="60"
        rx="6"
        fill="#22c55e"
        fillOpacity="0.2"
        stroke="#22c55e"
        strokeWidth="2"
      />
      <text x="195" y="40" textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="600">
        Staged
      </text>
      <text x="195" y="85" textAnchor="middle" fill="#94a3b8" fontSize="10">
        В индексе
      </text>
      <text x="195" y="100" textAnchor="middle" fill="#94a3b8" fontSize="10">
        A file.txt
      </text>

      {/* Arrow */}
      <path d="M250 90 L280 90" stroke="#f97316" strokeWidth="2" markerEnd="url(#arrow2)" />
      <text x="265" y="80" textAnchor="middle" fill="#f97316" fontSize="9">
        git commit
      </text>

      {/* Committed */}
      <rect
        x="290"
        y="60"
        width="90"
        height="60"
        rx="6"
        fill="#8b5cf6"
        fillOpacity="0.2"
        stroke="#8b5cf6"
        strokeWidth="2"
      />
      <text x="335" y="40" textAnchor="middle" fill="#8b5cf6" fontSize="11" fontWeight="600">
        Committed
      </text>
      <text x="335" y="85" textAnchor="middle" fill="#94a3b8" fontSize="10">
        В репозитории
      </text>
      <text x="335" y="100" textAnchor="middle" fill="#94a3b8" fontSize="10">
        Сохранено
      </text>

      {/* Modified */}
      <rect
        x="400"
        y="60"
        width="90"
        height="60"
        rx="6"
        fill="#eab308"
        fillOpacity="0.2"
        stroke="#eab308"
        strokeWidth="2"
      />
      <text x="445" y="40" textAnchor="middle" fill="#eab308" fontSize="11" fontWeight="600">
        Modified
      </text>
      <text x="445" y="85" textAnchor="middle" fill="#94a3b8" fontSize="10">
        Изменён
      </text>
      <text x="445" y="100" textAnchor="middle" fill="#94a3b8" fontSize="10">
        M file.txt
      </text>

      {/* Cycle arrow */}
      <path d="M380 90 L395 90" stroke="#eab308" strokeWidth="2" markerEnd="url(#arrow2)" />
      <text x="387" y="80" textAnchor="middle" fill="#eab308" fontSize="8">
        edit
      </text>

      {/* Back to staged arrow */}
      <path
        d="M445 130 L445 150 L195 150 L195 125"
        stroke="#f97316"
        strokeWidth="2"
        strokeDasharray="4"
        fill="none"
        markerEnd="url(#arrow2)"
      />
      <text x="320" y="165" textAnchor="middle" fill="#f97316" fontSize="9">
        git add (повторно)
      </text>

      {/* Arrow marker */}
      <defs>
        <marker
          id="arrow2"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L0,6 L9,3 z" fill="#f97316" />
        </marker>
      </defs>
    </svg>
  );
}

/**
 * Диаграмма режимов reset
 */
function ResetModesDiagram() {
  return (
    <svg width="500" height="220" viewBox="0 0 500 220" className="min-w-[450px]">
      {/* Header */}
      <text x="250" y="20" textAnchor="middle" fill="#e2e8f0" fontSize="14" fontWeight="600">
        Режимы git reset
      </text>

      {/* --soft */}
      <rect
        x="20"
        y="40"
        width="140"
        height="160"
        rx="8"
        fill="#22c55e"
        fillOpacity="0.1"
        stroke="#22c55e"
        strokeWidth="2"
      />
      <text x="90" y="60" textAnchor="middle" fill="#22c55e" fontSize="12" fontWeight="600">
        --soft
      </text>

      <rect
        x="35"
        y="75"
        width="110"
        height="30"
        rx="4"
        fill="#f97316"
        fillOpacity="0.3"
        stroke="#f97316"
      />
      <text x="90" y="95" textAnchor="middle" fill="#f97316" fontSize="10">
        HEAD ✓
      </text>

      <rect
        x="35"
        y="115"
        width="110"
        height="30"
        rx="4"
        fill="#8b5cf6"
        fillOpacity="0.3"
        stroke="#8b5cf6"
      />
      <text x="90" y="135" textAnchor="middle" fill="#8b5cf6" fontSize="10">
        Индекс ✓
      </text>

      <rect
        x="35"
        y="155"
        width="110"
        height="30"
        rx="4"
        fill="#06b6d4"
        fillOpacity="0.3"
        stroke="#06b6d4"
      />
      <text x="90" y="175" textAnchor="middle" fill="#06b6d4" fontSize="10">
        Рабочая директория ✓
      </text>

      <text x="90" y="200" textAnchor="middle" fill="#94a3b8" fontSize="9">
        Изменения сохранены
      </text>

      {/* --mixed (default) */}
      <rect
        x="180"
        y="40"
        width="140"
        height="160"
        rx="8"
        fill="#eab308"
        fillOpacity="0.1"
        stroke="#eab308"
        strokeWidth="2"
      />
      <text x="250" y="60" textAnchor="middle" fill="#eab308" fontSize="12" fontWeight="600">
        --mixed
      </text>

      <rect
        x="195"
        y="75"
        width="110"
        height="30"
        rx="4"
        fill="#f97316"
        fillOpacity="0.3"
        stroke="#f97316"
      />
      <text x="250" y="95" textAnchor="middle" fill="#f97316" fontSize="10">
        HEAD ✓
      </text>

      <rect
        x="195"
        y="115"
        width="110"
        height="30"
        rx="4"
        fill="#8b5cf6"
        fillOpacity="0.3"
        stroke="#8b5cf6"
      />
      <text x="250" y="135" textAnchor="middle" fill="#8b5cf6" fontSize="10">
        Индекс ✗
      </text>

      <rect
        x="195"
        y="155"
        width="110"
        height="30"
        rx="4"
        fill="#06b6d4"
        fillOpacity="0.3"
        stroke="#06b6d4"
      />
      <text x="250" y="175" textAnchor="middle" fill="#06b6d4" fontSize="10">
        Рабочая директория ✓
      </text>

      <text x="250" y="200" textAnchor="middle" fill="#94a3b8" fontSize="9">
        Изменения в рабочей директории
      </text>

      {/* --hard */}
      <rect
        x="340"
        y="40"
        width="140"
        height="160"
        rx="8"
        fill="#ef4444"
        fillOpacity="0.1"
        stroke="#ef4444"
        strokeWidth="2"
      />
      <text x="410" y="60" textAnchor="middle" fill="#ef4444" fontSize="12" fontWeight="600">
        --hard
      </text>

      <rect
        x="355"
        y="75"
        width="110"
        height="30"
        rx="4"
        fill="#f97316"
        fillOpacity="0.3"
        stroke="#f97316"
      />
      <text x="410" y="95" textAnchor="middle" fill="#f97316" fontSize="10">
        HEAD ✓
      </text>

      <rect
        x="355"
        y="115"
        width="110"
        height="30"
        rx="4"
        fill="#8b5cf6"
        fillOpacity="0.3"
        stroke="#8b5cf6"
      />
      <text x="410" y="135" textAnchor="middle" fill="#8b5cf6" fontSize="10">
        Индекс ✗
      </text>

      <rect
        x="355"
        y="155"
        width="110"
        height="30"
        rx="4"
        fill="#06b6d4"
        fillOpacity="0.3"
        stroke="#06b6d4"
      />
      <text x="410" y="175" textAnchor="middle" fill="#06b6d4" fontSize="10">
        Рабочая директория ✗
      </text>

      <text x="410" y="200" textAnchor="middle" fill="#94a3b8" fontSize="9">
        Все изменения потеряны!
      </text>
    </svg>
  );
}

/**
 * Диаграмма работы с remote
 */
function RemoteWorkflowDiagram() {
  return (
    <svg width="550" height="200" viewBox="0 0 550 200" className="min-w-[500px]">
      {/* Local */}
      <rect
        x="20"
        y="30"
        width="200"
        height="140"
        rx="8"
        fill="#06b6d4"
        fillOpacity="0.1"
        stroke="#06b6d4"
        strokeWidth="2"
      />
      <text x="120" y="50" textAnchor="middle" fill="#06b6d4" fontSize="14" fontWeight="600">
        Локальный репозиторий
      </text>

      {/* Local commits */}
      <circle cx="60" cy="100" r="8" fill="#06b6d4" />
      <text x="60" y="125" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C1
      </text>

      <circle cx="100" cy="100" r="8" fill="#06b6d4" />
      <text x="100" y="125" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C2
      </text>

      <circle cx="140" cy="100" r="8" fill="#06b6d4" />
      <text x="140" y="125" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C3
      </text>

      <circle cx="180" cy="100" r="8" fill="#22c55e" stroke="#16a34a" strokeWidth="2" />
      <text x="180" y="125" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C4 (новый)
      </text>

      <line x1="60" y1="100" x2="180" y2="100" stroke="#06b6d4" strokeWidth="2" />

      {/* Remote */}
      <rect
        x="330"
        y="30"
        width="200"
        height="140"
        rx="8"
        fill="#8b5cf6"
        fillOpacity="0.1"
        stroke="#8b5cf6"
        strokeWidth="2"
      />
      <text x="430" y="50" textAnchor="middle" fill="#8b5cf6" fontSize="14" fontWeight="600">
        Remote (origin)
      </text>

      {/* Remote commits */}
      <circle cx="370" cy="100" r="8" fill="#8b5cf6" />
      <text x="370" y="125" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C1
      </text>

      <circle cx="410" cy="100" r="8" fill="#8b5cf6" />
      <text x="410" y="125" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C2
      </text>

      <circle cx="450" cy="100" r="8" fill="#8b5cf6" />
      <text x="450" y="125" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C3
      </text>

      <line x1="370" y1="100" x2="450" y2="100" stroke="#8b5cf6" strokeWidth="2" />

      {/* Arrows */}
      {/* Push */}
      <path d="M230 80 L320 80" stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrowGreen)" />
      <text x="275" y="70" textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="600">
        git push
      </text>

      {/* Pull */}
      <path d="M320 120 L230 120" stroke="#f97316" strokeWidth="2" markerEnd="url(#arrowOrange)" />
      <text x="275" y="140" textAnchor="middle" fill="#f97316" fontSize="11" fontWeight="600">
        git pull
      </text>

      {/* Fetch */}
      <path
        d="M320 160 L230 160"
        stroke="#06b6d4"
        strokeWidth="2"
        strokeDasharray="4"
        markerEnd="url(#arrowCyan)"
      />
      <text x="275" y="180" textAnchor="middle" fill="#06b6d4" fontSize="11">
        git fetch
      </text>

      {/* Arrow markers */}
      <defs>
        <marker id="arrowGreen" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <path d="M0,0 L0,6 L9,3 z" fill="#22c55e" />
        </marker>
        <marker id="arrowOrange" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <path d="M0,0 L0,6 L9,3 z" fill="#f97316" />
        </marker>
        <marker id="arrowCyan" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <path d="M0,0 L0,6 L9,3 z" fill="#06b6d4" />
        </marker>
      </defs>
    </svg>
  );
}

/**
 * Диаграмма rebase vs merge
 */
function RebaseVsMergeDiagram() {
  return (
    <svg width="550" height="280" viewBox="0 0 550 280" className="min-w-[500px]">
      {/* Merge section */}
      <text x="135" y="20" textAnchor="middle" fill="#e2e8f0" fontSize="14" fontWeight="600">
        Merge
      </text>

      {/* Main branch */}
      <line x1="30" y1="60" x2="240" y2="60" stroke="#f97316" strokeWidth="3" />
      <text x="10" y="65" fill="#f97316" fontSize="10">
        main
      </text>

      <circle cx="60" cy="60" r="6" fill="#f97316" />
      <circle cx="120" cy="60" r="6" fill="#f97316" />
      <circle cx="180" cy="60" r="8" fill="#22c55e" stroke="#16a34a" strokeWidth="2" />
      <text x="180" y="85" textAnchor="middle" fill="#94a3b8" fontSize="9">
        merge commit
      </text>
      <circle cx="240" cy="60" r="6" fill="#f97316" />

      {/* Feature branch */}
      <path d="M60 60 L60 120 L150 120" stroke="#06b6d4" strokeWidth="2" fill="none" />
      <text x="155" y="125" fill="#06b6d4" fontSize="10">
        feature
      </text>

      <circle cx="90" cy="120" r="5" fill="#06b6d4" />
      <circle cx="120" cy="120" r="5" fill="#06b6d4" />
      <circle cx="150" cy="120" r="5" fill="#06b6d4" />

      {/* Merge arrow */}
      <path d="M150 120 L180 60" stroke="#06b6d4" strokeWidth="2" strokeDasharray="4" />

      {/* Rebase section */}
      <text x="410" y="20" textAnchor="middle" fill="#e2e8f0" fontSize="14" fontWeight="600">
        Rebase
      </text>

      {/* Main branch */}
      <line x1="310" y1="60" x2="520" y2="60" stroke="#f97316" strokeWidth="3" />
      <text x="290" y="65" fill="#f97316" fontSize="10">
        main
      </text>

      <circle cx="340" cy="60" r="6" fill="#f97316" />
      <circle cx="400" cy="60" r="6" fill="#f97316" />
      <circle cx="460" cy="60" r="6" fill="#06b6d4" />
      <circle cx="490" cy="60" r="6" fill="#06b6d4" />
      <circle cx="520" cy="60" r="6" fill="#06b6d4" />

      {/* Rebased feature commits */}
      <text x="460" y="85" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C4'
      </text>
      <text x="490" y="85" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C5'
      </text>
      <text x="520" y="85" textAnchor="middle" fill="#94a3b8" fontSize="9">
        C6'
      </text>

      {/* Old feature branch (faded) */}
      <path
        d="M340 60 L340 120 L430 120"
        stroke="#06b6d4"
        strokeWidth="2"
        strokeOpacity="0.3"
        fill="none"
        strokeDasharray="4"
      />
      <circle cx="370" cy="120" r="5" fill="#06b6d4" fillOpacity="0.3" />
      <circle cx="400" cy="120" r="5" fill="#06b6d4" fillOpacity="0.3" />
      <circle cx="430" cy="120" r="5" fill="#06b6d4" fillOpacity="0.3" />

      <text x="370" y="145" textAnchor="middle" fill="#94a3b8" fontSize="8" fillOpacity="0.5">
        C4
      </text>
      <text x="400" y="145" textAnchor="middle" fill="#94a3b8" fontSize="8" fillOpacity="0.5">
        C5
      </text>
      <text x="430" y="145" textAnchor="middle" fill="#94a3b8" fontSize="8" fillOpacity="0.5">
        C6
      </text>

      {/* Legend */}
      <rect x="30" y="180" width="230" height="80" rx="4" fill="#0f172a" stroke="#475569" />
      <text x="145" y="200" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="600">
        Merge
      </text>
      <text x="145" y="220" textAnchor="middle" fill="#94a3b8" fontSize="10">
        + Сохраняет историю
      </text>
      <text x="145" y="240" textAnchor="middle" fill="#94a3b8" fontSize="10">
        + Видны все ветки
      </text>
      <text x="145" y="260" textAnchor="middle" fill="#94a3b8" fontSize="10">
        - Много merge-коммитов
      </text>

      <rect x="290" y="180" width="230" height="80" rx="4" fill="#0f172a" stroke="#475569" />
      <text x="405" y="200" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="600">
        Rebase
      </text>
      <text x="405" y="220" textAnchor="middle" fill="#94a3b8" fontSize="10">
        + Линейная история
      </text>
      <text x="405" y="240" textAnchor="middle" fill="#94a3b8" fontSize="10">
        + Чистый граф
      </text>
      <text x="405" y="260" textAnchor="middle" fill="#ef4444" fontSize="10">
        - Переписывает историю
      </text>
    </svg>
  );
}

/**
 * Диаграмма процесса git init
 */
function GitInitProcessDiagram() {
  return (
    <svg width="500" height="220" viewBox="0 0 500 220" className="min-w-[450px]">
      {/* Before */}
      <rect
        x="20"
        y="30"
        width="180"
        height="160"
        rx="8"
        fill="#0f172a"
        stroke="#475569"
        strokeWidth="2"
      />
      <text x="110" y="55" textAnchor="middle" fill="#94a3b8" fontSize="12" fontWeight="600">
        До git init
      </text>

      {/* Folder icon */}
      <rect x="70" y="80" width="80" height="60" rx="4" fill="#1e293b" stroke="#475569" />
      <text x="110" y="115" textAnchor="middle" fill="#e2e8f0" fontSize="11">
        my-project/
      </text>
      <text x="110" y="135" textAnchor="middle" fill="#64748b" fontSize="9">
        Обычная папка
      </text>

      {/* Arrow */}
      <path d="M220 110 L280 110" stroke="#22c55e" strokeWidth="3" markerEnd="url(#arrowGreen3)" />
      <text x="250" y="95" textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="600">
        git init
      </text>

      {/* After */}
      <rect
        x="300"
        y="30"
        width="180"
        height="160"
        rx="8"
        fill="#22c55e"
        fillOpacity="0.1"
        stroke="#22c55e"
        strokeWidth="2"
      />
      <text x="390" y="55" textAnchor="middle" fill="#22c55e" fontSize="12" fontWeight="600">
        После git init
      </text>

      {/* Folder with .git */}
      <rect x="340" y="70" width="100" height="80" rx="4" fill="#1e293b" stroke="#22c55e" />
      <text x="390" y="95" textAnchor="middle" fill="#e2e8f0" fontSize="10">
        my-project/
      </text>

      {/* .git folder */}
      <rect
        x="350"
        y="105"
        width="80"
        height="35"
        rx="3"
        fill="#22c55e"
        fillOpacity="0.2"
        stroke="#22c55e"
      />
      <text x="390" y="127" textAnchor="middle" fill="#22c55e" fontSize="9">
        .git/
      </text>

      {/* Created files list */}
      <text x="390" y="170" textAnchor="middle" fill="#64748b" fontSize="8">
        HEAD, config, objects/, refs/
      </text>

      {/* Arrow marker */}
      <defs>
        <marker id="arrowGreen3" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <path d="M0,0 L0,6 L9,3 z" fill="#22c55e" />
        </marker>
      </defs>
    </svg>
  );
}

/**
 * Диаграмма процесса git add
 */
function GitAddProcessDiagram() {
  return (
    <svg width="550" height="200" viewBox="0 0 550 200" className="min-w-[500px]">
      {/* Working Directory */}
      <rect
        x="20"
        y="40"
        width="150"
        height="120"
        rx="8"
        fill="#06b6d4"
        fillOpacity="0.1"
        stroke="#06b6d4"
        strokeWidth="2"
      />
      <text x="95" y="25" textAnchor="middle" fill="#06b6d4" fontSize="12" fontWeight="600">
        Working Directory
      </text>

      {/* Files in WD */}
      <rect
        x="35"
        y="60"
        width="55"
        height="30"
        rx="4"
        fill="#ef4444"
        fillOpacity="0.2"
        stroke="#ef4444"
      />
      <text x="62" y="80" textAnchor="middle" fill="#ef4444" fontSize="9">
        new.txt
      </text>
      <text x="62" y="95" textAnchor="middle" fill="#64748b" fontSize="7">
        ?? (untracked)
      </text>

      <rect
        x="100"
        y="60"
        width="55"
        height="30"
        rx="4"
        fill="#eab308"
        fillOpacity="0.2"
        stroke="#eab308"
      />
      <text x="127" y="80" textAnchor="middle" fill="#eab308" fontSize="9">
        mod.txt
      </text>
      <text x="127" y="95" textAnchor="middle" fill="#64748b" fontSize="7">
        M (modified)
      </text>

      <rect
        x="35"
        y="110"
        width="120"
        height="30"
        rx="4"
        fill="#64748b"
        fillOpacity="0.2"
        stroke="#64748b"
      />
      <text x="95" y="130" textAnchor="middle" fill="#64748b" fontSize="9">
        unchanged.txt
      </text>

      {/* Arrow */}
      <path d="M180 100 L230 100" stroke="#f97316" strokeWidth="3" markerEnd="url(#arrowOrange3)" />
      <text x="205" y="85" textAnchor="middle" fill="#f97316" fontSize="11" fontWeight="600">
        git add
      </text>
      <text x="205" y="120" textAnchor="middle" fill="#64748b" fontSize="8">
        . или файл
      </text>

      {/* Staging Area */}
      <rect
        x="240"
        y="40"
        width="150"
        height="120"
        rx="8"
        fill="#8b5cf6"
        fillOpacity="0.1"
        stroke="#8b5cf6"
        strokeWidth="2"
      />
      <text x="315" y="25" textAnchor="middle" fill="#8b5cf6" fontSize="12" fontWeight="600">
        Staging Area
      </text>

      {/* Files in Staging */}
      <rect
        x="255"
        y="60"
        width="55"
        height="30"
        rx="4"
        fill="#22c55e"
        fillOpacity="0.2"
        stroke="#22c55e"
      />
      <text x="282" y="80" textAnchor="middle" fill="#22c55e" fontSize="9">
        new.txt
      </text>
      <text x="282" y="95" textAnchor="middle" fill="#64748b" fontSize="7">
        A (added)
      </text>

      <rect
        x="320"
        y="60"
        width="55"
        height="30"
        rx="4"
        fill="#22c55e"
        fillOpacity="0.2"
        stroke="#22c55e"
      />
      <text x="347" y="80" textAnchor="middle" fill="#22c55e" fontSize="9">
        mod.txt
      </text>
      <text x="347" y="95" textAnchor="middle" fill="#64748b" fontSize="7">
        M (staged)
      </text>

      <text x="315" y="140" textAnchor="middle" fill="#64748b" fontSize="9">
        Готовы к коммиту
      </text>

      {/* Arrow to repo */}
      <path d="M400 100 L450 100" stroke="#22c55e" strokeWidth="3" markerEnd="url(#arrowGreen4)" />
      <text x="425" y="85" textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="600">
        git commit
      </text>

      {/* Repository */}
      <rect
        x="460"
        y="40"
        width="80"
        height="120"
        rx="8"
        fill="#f97316"
        fillOpacity="0.1"
        stroke="#f97316"
        strokeWidth="2"
      />
      <text x="500" y="25" textAnchor="middle" fill="#f97316" fontSize="12" fontWeight="600">
        Repository
      </text>
      <circle cx="500" cy="100" r="20" fill="#f97316" fillOpacity="0.3" stroke="#f97316" />
      <text x="500" y="105" textAnchor="middle" fill="#f97316" fontSize="10">
        C1
      </text>

      {/* Arrow markers */}
      <defs>
        <marker
          id="arrowOrange3"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L0,6 L9,3 z" fill="#f97316" />
        </marker>
        <marker id="arrowGreen4" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <path d="M0,0 L0,6 L9,3 z" fill="#22c55e" />
        </marker>
      </defs>
    </svg>
  );
}

/**
 * Диаграмма процесса git commit
 */
function GitCommitProcessDiagram() {
  return (
    <svg width="500" height="250" viewBox="0 0 500 250" className="min-w-[450px]">
      {/* Header */}
      <text x="250" y="20" textAnchor="middle" fill="#e2e8f0" fontSize="14" fontWeight="600">
        Что происходит при git commit
      </text>

      {/* Before commit */}
      <rect
        x="20"
        y="40"
        width="200"
        height="180"
        rx="8"
        fill="#8b5cf6"
        fillOpacity="0.1"
        stroke="#8b5cf6"
        strokeWidth="2"
      />
      <text x="120" y="60" textAnchor="middle" fill="#8b5cf6" fontSize="11" fontWeight="600">
        До коммита
      </text>

      {/* Staged files */}
      <rect x="35" y="75" width="170" height="50" rx="4" fill="#1e293b" stroke="#8b5cf6" />
      <text x="120" y="95" textAnchor="middle" fill="#e2e8f0" fontSize="10">
        Staging Area:
      </text>
      <text x="120" y="115" textAnchor="middle" fill="#22c55e" fontSize="9">
        + file1.txt, file2.ts
      </text>

      {/* Tree and blob objects */}
      <rect x="35" y="135" width="170" height="70" rx="4" fill="#1e293b" stroke="#64748b" />
      <text x="120" y="155" textAnchor="middle" fill="#94a3b8" fontSize="9">
        Git создаёт объекты:
      </text>
      <text x="120" y="175" textAnchor="middle" fill="#06b6d4" fontSize="8">
        blob (содержимое файлов)
      </text>
      <text x="120" y="190" textAnchor="middle" fill="#06b6d4" fontSize="8">
        tree (структура директорий)
      </text>

      {/* Arrow */}
      <path d="M240 130 L280 130" stroke="#22c55e" strokeWidth="3" markerEnd="url(#arrowGreen5)" />
      <text x="260" y="115" textAnchor="middle" fill="#22c55e" fontSize="10" fontWeight="600">
        commit
      </text>

      {/* After commit */}
      <rect
        x="280"
        y="40"
        width="200"
        height="180"
        rx="8"
        fill="#22c55e"
        fillOpacity="0.1"
        stroke="#22c55e"
        strokeWidth="2"
      />
      <text x="380" y="60" textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="600">
        После коммита
      </text>

      {/* Commit object */}
      <rect x="300" y="75" width="160" height="130" rx="6" fill="#1e293b" stroke="#22c55e" />
      <text x="380" y="95" textAnchor="middle" fill="#22c55e" fontSize="10" fontWeight="600">
        Commit Object
      </text>

      <text x="310" y="115" fill="#94a3b8" fontSize="8">
        SHA: a1b2c3d4...
      </text>
      <text x="310" y="130" fill="#94a3b8" fontSize="8">
        Author: Иван
      </text>
      <text x="310" y="145" fill="#94a3b8" fontSize="8">
        Date: 2024-01-15
      </text>
      <text x="310" y="160" fill="#e2e8f0" fontSize="8">
        Message: "Add feature"
      </text>
      <text x="310" y="175" fill="#64748b" fontSize="8">
        Parent: предыдущий коммит
      </text>
      <text x="310" y="190" fill="#06b6d4" fontSize="8">
        → tree → blobs
      </text>

      {/* Arrow marker */}
      <defs>
        <marker id="arrowGreen5" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <path d="M0,0 L0,6 L9,3 z" fill="#22c55e" />
        </marker>
      </defs>
    </svg>
  );
}

/**
 * Диаграмма операций с ветками
 */
function BranchOperationsDiagram() {
  return (
    <svg width="550" height="280" viewBox="0 0 550 280" className="min-w-[500px]">
      {/* Create branch */}
      <rect
        x="20"
        y="20"
        width="160"
        height="120"
        rx="8"
        fill="#06b6d4"
        fillOpacity="0.1"
        stroke="#06b6d4"
        strokeWidth="2"
      />
      <text x="100" y="40" textAnchor="middle" fill="#06b6d4" fontSize="11" fontWeight="600">
        git branch feature
      </text>

      <line x1="40" y1="70" x2="140" y2="70" stroke="#f97316" strokeWidth="3" />
      <circle cx="60" cy="70" r="6" fill="#f97316" />
      <circle cx="100" cy="70" r="6" fill="#f97316" />
      <text x="30" y="75" fill="#f97316" fontSize="9">
        main
      </text>

      <path d="M100 70 L100 100 L140 100" stroke="#06b6d4" strokeWidth="2" strokeDasharray="4" />
      <circle cx="120" cy="100" r="5" fill="#06b6d4" strokeDasharray="2" />
      <text x="145" y="105" fill="#06b6d4" fontSize="9">
        feature
      </text>

      {/* Switch branch */}
      <rect
        x="200"
        y="20"
        width="160"
        height="120"
        rx="8"
        fill="#8b5cf6"
        fillOpacity="0.1"
        stroke="#8b5cf6"
        strokeWidth="2"
      />
      <text x="280" y="40" textAnchor="middle" fill="#8b5cf6" fontSize="11" fontWeight="600">
        git checkout feature
      </text>

      <line
        x1="220"
        y1="70"
        x2="320"
        y2="70"
        stroke="#f97316"
        strokeWidth="2"
        strokeOpacity="0.5"
      />
      <circle cx="240" cy="70" r="5" fill="#f97316" fillOpacity="0.5" />
      <circle cx="280" cy="70" r="5" fill="#f97316" fillOpacity="0.5" />
      <text x="210" y="75" fill="#f97316" fontSize="9" fillOpacity="0.5">
        main
      </text>

      <line x1="260" y1="100" x2="320" y2="100" stroke="#06b6d4" strokeWidth="3" />
      <circle cx="280" cy="100" r="6" fill="#06b6d4" />
      <circle cx="310" cy="100" r="6" fill="#06b6d4" />
      <text x="250" y="105" fill="#06b6d4" fontSize="9">
        feature
      </text>
      <text x="330" y="105" fill="#22c55e" fontSize="9">
        HEAD
      </text>

      {/* Delete branch */}
      <rect
        x="380"
        y="20"
        width="160"
        height="120"
        rx="8"
        fill="#ef4444"
        fillOpacity="0.1"
        stroke="#ef4444"
        strokeWidth="2"
      />
      <text x="460" y="40" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="600">
        git branch -d feature
      </text>

      <line x1="400" y1="70" x2="520" y2="70" stroke="#f97316" strokeWidth="3" />
      <circle cx="420" cy="70" r="6" fill="#f97316" />
      <circle cx="460" cy="70" r="6" fill="#f97316" />
      <circle cx="500" cy="70" r="6" fill="#f97316" />
      <text x="390" y="75" fill="#f97316" fontSize="9">
        main
      </text>

      <text
        x="460"
        y="110"
        textAnchor="middle"
        fill="#64748b"
        fontSize="9"
        textDecoration="line-through"
      >
        feature удалена
      </text>

      {/* Commands reference */}
      <rect x="20" y="160" width="520" height="100" rx="8" fill="#0f172a" stroke="#475569" />
      <text x="280" y="180" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="600">
        Команды для работы с ветками
      </text>

      <text x="40" y="205" fill="#06b6d4" fontSize="9">
        git branch
      </text>
      <text x="130" y="205" fill="#94a3b8" fontSize="9">
        — список веток
      </text>

      <text x="40" y="225" fill="#06b6d4" fontSize="9">
        git branch name
      </text>
      <text x="150" y="225" fill="#94a3b8" fontSize="9">
        — создать ветку
      </text>

      <text x="40" y="245" fill="#06b6d4" fontSize="9">
        git checkout -b name
      </text>
      <text x="175" y="245" fill="#94a3b8" fontSize="9">
        — создать и переключиться
      </text>

      <text x="300" y="205" fill="#8b5cf6" fontSize="9">
        git switch name
      </text>
      <text x="410" y="205" fill="#94a3b8" fontSize="9">
        — переключиться
      </text>

      <text x="300" y="225" fill="#ef4444" fontSize="9">
        git branch -d name
      </text>
      <text x="420" y="225" fill="#94a3b8" fontSize="9">
        — удалить ветку
      </text>

      <text x="300" y="245" fill="#22c55e" fontSize="9">
        git branch -m old new
      </text>
      <text x="430" y="245" fill="#94a3b8" fontSize="9">
        — переименовать
      </text>
    </svg>
  );
}

/**
 * Диаграмма работы git stash
 */
function StashWorkflowDiagram() {
  return (
    <svg width="500" height="250" viewBox="0 0 500 250" className="min-w-[450px]">
      {/* Working Directory with changes */}
      <rect
        x="20"
        y="30"
        width="140"
        height="100"
        rx="8"
        fill="#eab308"
        fillOpacity="0.1"
        stroke="#eab308"
        strokeWidth="2"
      />
      <text x="90" y="50" textAnchor="middle" fill="#eab308" fontSize="11" fontWeight="600">
        Working Dir
      </text>
      <text x="90" y="75" textAnchor="middle" fill="#eab308" fontSize="9">
        Изменения:
      </text>
      <text x="90" y="95" textAnchor="middle" fill="#94a3b8" fontSize="8">
        file1.ts (M)
      </text>
      <text x="90" y="110" textAnchor="middle" fill="#94a3b8" fontSize="8">
        file2.ts (M)
      </text>

      {/* Arrow to stash */}
      <path d="M170 80 L210 80" stroke="#8b5cf6" strokeWidth="2" markerEnd="url(#arrowPurple)" />
      <text x="190" y="70" textAnchor="middle" fill="#8b5cf6" fontSize="9">
        stash
      </text>

      {/* Stash stack */}
      <rect
        x="220"
        y="30"
        width="100"
        height="100"
        rx="8"
        fill="#8b5cf6"
        fillOpacity="0.1"
        stroke="#8b5cf6"
        strokeWidth="2"
      />
      <text x="270" y="50" textAnchor="middle" fill="#8b5cf6" fontSize="11" fontWeight="600">
        Stash
      </text>

      <rect
        x="235"
        y="60"
        width="70"
        height="20"
        rx="3"
        fill="#8b5cf6"
        fillOpacity="0.3"
        stroke="#8b5cf6"
      />
      <text x="270" y="74" textAnchor="middle" fill="#e2e8f0" fontSize="8">
        stash@{0}
      </text>

      <rect
        x="235"
        y="85"
        width="70"
        height="20"
        rx="3"
        fill="#8b5cf6"
        fillOpacity="0.2"
        stroke="#8b5cf6"
        strokeOpacity="0.5"
      />
      <text x="270" y="99" textAnchor="middle" fill="#94a3b8" fontSize="8">
        stash@{1}
      </text>

      {/* Clean Working Dir */}
      <rect
        x="20"
        y="150"
        width="140"
        height="80"
        rx="8"
        fill="#22c55e"
        fillOpacity="0.1"
        stroke="#22c55e"
        strokeWidth="2"
      />
      <text x="90" y="170" textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="600">
        Working Dir
      </text>
      <text x="90" y="195" textAnchor="middle" fill="#22c55e" fontSize="9">
        Чистый!
      </text>
      <text x="90" y="215" textAnchor="middle" fill="#64748b" fontSize="8">
        Можно переключать ветку
      </text>

      {/* Arrow from stash */}
      <path d="M330 80 L370 80" stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrowGreen6)" />
      <text x="350" y="70" textAnchor="middle" fill="#22c55e" fontSize="9">
        pop
      </text>

      {/* Restored Working Dir */}
      <rect
        x="380"
        y="30"
        width="100"
        height="100"
        rx="8"
        fill="#eab308"
        fillOpacity="0.1"
        stroke="#eab308"
        strokeWidth="2"
      />
      <text x="430" y="50" textAnchor="middle" fill="#eab308" fontSize="11" fontWeight="600">
        Восстановлено
      </text>
      <text x="430" y="75" textAnchor="middle" fill="#94a3b8" fontSize="8">
        file1.ts (M)
      </text>
      <text x="430" y="90" textAnchor="middle" fill="#94a3b8" fontSize="8">
        file2.ts (M)
      </text>
      <text x="430" y="115" textAnchor="middle" fill="#22c55e" fontSize="8">
        stash@{0} удалён
      </text>

      {/* Commands */}
      <rect x="220" y="150" width="260" height="80" rx="8" fill="#0f172a" stroke="#475569" />
      <text x="350" y="170" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">
        Команды
      </text>

      <text x="235" y="190" fill="#8b5cf6" fontSize="8">
        git stash
      </text>
      <text x="310" y="190" fill="#94a3b8" fontSize="8">
        — спрятать изменения
      </text>

      <text x="235" y="205" fill="#22c55e" fontSize="8">
        git stash pop
      </text>
      <text x="330" y="205" fill="#94a3b8" fontSize="8">
        — вернуть и удалить
      </text>

      <text x="235" y="220" fill="#06b6d4" fontSize="8">
        git stash list
      </text>
      <text x="330" y="220" fill="#94a3b8" fontSize="8">
        — список stash
      </text>

      {/* Arrow markers */}
      <defs>
        <marker id="arrowPurple" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <path d="M0,0 L0,6 L9,3 z" fill="#8b5cf6" />
        </marker>
        <marker id="arrowGreen6" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <path d="M0,0 L0,6 L9,3 z" fill="#22c55e" />
        </marker>
      </defs>
    </svg>
  );
}

/**
 * Диаграмма cherry-pick
 */
function CherryPickFlowDiagram() {
  return (
    <svg width="500" height="220" viewBox="0 0 500 220" className="min-w-[450px]">
      {/* Source branch */}
      <rect
        x="20"
        y="30"
        width="200"
        height="80"
        rx="8"
        fill="#06b6d4"
        fillOpacity="0.1"
        stroke="#06b6d4"
        strokeWidth="2"
      />
      <text x="120" y="50" textAnchor="middle" fill="#06b6d4" fontSize="11" fontWeight="600">
        feature branch
      </text>

      <line x1="40" y1="80" x2="200" y2="80" stroke="#06b6d4" strokeWidth="2" />
      <circle cx="60" cy="80" r="5" fill="#06b6d4" />
      <circle cx="100" cy="80" r="5" fill="#06b6d4" />
      <circle cx="140" cy="80" r="7" fill="#f97316" stroke="#f97316" strokeWidth="2" />
      <circle cx="180" cy="80" r="5" fill="#06b6d4" />

      <text x="140" y="100" textAnchor="middle" fill="#f97316" fontSize="8">
        C3 (нужен)
      </text>

      {/* Arrow */}
      <path
        d="M140 110 L140 140 L260 140 L260 80 L300 80"
        stroke="#f97316"
        strokeWidth="2"
        strokeDasharray="4"
        markerEnd="url(#arrowOrange4)"
      />
      <text x="200" y="135" textAnchor="middle" fill="#f97316" fontSize="10" fontWeight="600">
        cherry-pick C3
      </text>

      {/* Target branch */}
      <rect
        x="280"
        y="30"
        width="200"
        height="80"
        rx="8"
        fill="#f97316"
        fillOpacity="0.1"
        stroke="#f97316"
        strokeWidth="2"
      />
      <text x="380" y="50" textAnchor="middle" fill="#f97316" fontSize="11" fontWeight="600">
        main branch
      </text>

      <line x1="300" y1="80" x2="460" y2="80" stroke="#f97316" strokeWidth="2" />
      <circle cx="320" cy="80" r="5" fill="#f97316" />
      <circle cx="360" cy="80" r="5" fill="#f97316" />
      <circle cx="420" cy="80" r="7" fill="#22c55e" stroke="#16a34a" strokeWidth="2" />

      <text x="420" y="100" textAnchor="middle" fill="#22c55e" fontSize="8">
        C3' (копия)
      </text>

      {/* Note */}
      <rect x="20" y="140" width="460" height="60" rx="8" fill="#0f172a" stroke="#475569" />
      <text x="250" y="160" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">
        Важно!
      </text>
      <text x="250" y="180" textAnchor="middle" fill="#94a3b8" fontSize="9">
        Cherry-pick создаёт НОВЫЙ коммит с другим SHA
      </text>
      <text x="250" y="195" textAnchor="middle" fill="#ef4444" fontSize="8">
        Не используйте для коммитов, которые уже отправлены!
      </text>

      {/* Arrow marker */}
      <defs>
        <marker
          id="arrowOrange4"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L0,6 L9,3 z" fill="#f97316" />
        </marker>
      </defs>
    </svg>
  );
}

/**
 * Диаграмма разрешения конфликтов
 */
function ConflictResolutionDiagram() {
  return (
    <svg width="550" height="280" viewBox="0 0 550 280" className="min-w-[500px]">
      {/* Conflict situation */}
      <text x="275" y="20" textAnchor="middle" fill="#ef4444" fontSize="14" fontWeight="600">
        Конфликт при слиянии
      </text>

      {/* Main branch */}
      <line x1="50" y1="60" x2="250" y2="60" stroke="#f97316" strokeWidth="3" />
      <text x="30" y="65" fill="#f97316" fontSize="10">
        main
      </text>
      <circle cx="100" cy="60" r="6" fill="#f97316" />
      <circle cx="150" cy="60" r="6" fill="#f97316" />
      <circle cx="200" cy="60" r="8" fill="#ef4444" stroke="#dc2626" strokeWidth="2" />
      <text x="200" y="85" textAnchor="middle" fill="#ef4444" fontSize="9">
        merge commit
      </text>

      {/* Feature branch */}
      <path d="M100 60 L100 120 L180 120" stroke="#06b6d4" strokeWidth="2" fill="none" />
      <text x="185" y="125" fill="#06b6d4" fontSize="10">
        feature
      </text>
      <circle cx="130" cy="120" r="5" fill="#06b6d4" />
      <circle cx="160" cy="120" r="5" fill="#06b6d4" />

      {/* Conflict marker */}
      <rect
        x="260"
        y="40"
        width="130"
        height="90"
        rx="6"
        fill="#ef4444"
        fillOpacity="0.1"
        stroke="#ef4444"
      />
      <text x="325" y="60" textAnchor="middle" fill="#ef4444" fontSize="10" fontWeight="600">
        Конфликт!
      </text>
      <text x="325" y="80" textAnchor="middle" fill="#94a3b8" fontSize="8">
        Обе ветки изменили
      </text>
      <text x="325" y="95" textAnchor="middle" fill="#94a3b8" fontSize="8">
        одни и те же строки
      </text>
      <text x="325" y="115" textAnchor="middle" fill="#eab308" fontSize="8">
        в file.txt
      </text>

      {/* Resolution steps */}
      <rect x="20" y="150" width="510" height="110" rx="8" fill="#0f172a" stroke="#475569" />
      <text x="275" y="170" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="600">
        Шаги разрешения конфликта
      </text>

      {/* Step 1 */}
      <circle cx="50" cy="195" r="12" fill="#22c55e" fillOpacity="0.2" stroke="#22c55e" />
      <text x="50" y="199" textAnchor="middle" fill="#22c55e" fontSize="10">
        1
      </text>
      <text x="70" y="199" fill="#94a3b8" fontSize="9">
        Откройте файл с конфликтом
      </text>

      {/* Step 2 */}
      <circle cx="50" cy="220" r="12" fill="#22c55e" fillOpacity="0.2" stroke="#22c55e" />
      <text x="50" y="224" textAnchor="middle" fill="#22c55e" fontSize="10">
        2
      </text>
      <text x="70" y="224" fill="#94a3b8" fontSize="9">
        Найдите маркеры {'<<<<<'} HEAD и {'>>>>>'} branch
      </text>

      {/* Step 3 */}
      <circle cx="280" cy="195" r="12" fill="#22c55e" fillOpacity="0.2" stroke="#22c55e" />
      <text x="280" y="199" textAnchor="middle" fill="#22c55e" fontSize="10">
        3
      </text>
      <text x="300" y="199" fill="#94a3b8" fontSize="9">
        Отредактируйте код, оставив нужное
      </text>

      {/* Step 4 */}
      <circle cx="280" cy="220" r="12" fill="#22c55e" fillOpacity="0.2" stroke="#22c55e" />
      <text x="280" y="224" textAnchor="middle" fill="#22c55e" fontSize="10">
        4
      </text>
      <text x="300" y="224" fill="#94a3b8" fontSize="9">
        git add file.txt && git commit
      </text>

      {/* Conflict markers example */}
      <rect x="400" y="180" width="120" height="65" rx="4" fill="#1e293b" stroke="#475569" />
      <text x="410" y="195" fill="#ef4444" fontSize="7">
        {'<<<<<'} HEAD
      </text>
      <text x="410" y="207" fill="#e2e8f0" fontSize="7">
        main code
      </text>
      <text x="410" y="219" fill="#eab308" fontSize="7">
        =======
      </text>
      <text x="410" y="231" fill="#e2e8f0" fontSize="7">
        feature code
      </text>
      <text x="410" y="243" fill="#06b6d4" fontSize="7">
        {'>>>>>'} feature
      </text>
    </svg>
  );
}

/**
 * Диаграмма движения HEAD
 */
function HeadMovementDiagram() {
  return (
    <svg width="550" height="280" viewBox="0 0 550 280" className="min-w-[500px]">
      {/* Scenario 1: HEAD on branch */}
      <rect
        x="20"
        y="20"
        width="160"
        height="120"
        rx="8"
        fill="#22c55e"
        fillOpacity="0.1"
        stroke="#22c55e"
        strokeWidth="2"
      />
      <text x="100" y="40" textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="600">
        HEAD на ветке
      </text>

      <line x1="40" y1="80" x2="140" y2="80" stroke="#f97316" strokeWidth="2" />
      <circle cx="60" cy="80" r="5" fill="#f97316" />
      <circle cx="100" cy="80" r="5" fill="#f97316" />
      <circle cx="130" cy="80" r="6" fill="#f97316" />
      <text x="30" y="85" fill="#f97316" fontSize="8">
        main
      </text>

      {/* HEAD pointer */}
      <rect x="115" y="95" width="40" height="20" rx="3" fill="#22c55e" />
      <text x="135" y="109" textAnchor="middle" fill="white" fontSize="8" fontWeight="600">
        HEAD
      </text>
      <path d="M135 95 L135 86" stroke="#22c55e" strokeWidth="2" />

      <text x="100" y="130" textAnchor="middle" fill="#94a3b8" fontSize="8">
        git checkout main
      </text>

      {/* Scenario 2: Detached HEAD */}
      <rect
        x="200"
        y="20"
        width="160"
        height="120"
        rx="8"
        fill="#ef4444"
        fillOpacity="0.1"
        stroke="#ef4444"
        strokeWidth="2"
      />
      <text x="280" y="40" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="600">
        Detached HEAD
      </text>

      <line x1="220" y1="80" x2="340" y2="80" stroke="#f97316" strokeWidth="2" />
      <circle cx="240" cy="80" r="5" fill="#f97316" />
      <circle cx="270" cy="80" r="6" fill="#ef4444" stroke="#dc2626" strokeWidth="2" />
      <circle cx="300" cy="80" r="5" fill="#f97316" />
      <circle cx="330" cy="80" r="5" fill="#f97316" />
      <text x="210" y="85" fill="#f97316" fontSize="8">
        main
      </text>

      {/* HEAD pointer detached */}
      <rect x="255" y="95" width="40" height="20" rx="3" fill="#ef4444" />
      <text x="275" y="109" textAnchor="middle" fill="white" fontSize="8" fontWeight="600">
        HEAD
      </text>
      <path d="M275 95 L275 86" stroke="#ef4444" strokeWidth="2" />

      <text x="280" y="130" textAnchor="middle" fill="#94a3b8" fontSize="8">
        git checkout abc123
      </text>

      {/* Scenario 3: HEAD after commit */}
      <rect
        x="380"
        y="20"
        width="160"
        height="120"
        rx="8"
        fill="#8b5cf6"
        fillOpacity="0.1"
        stroke="#8b5cf6"
        strokeWidth="2"
      />
      <text x="460" y="40" textAnchor="middle" fill="#8b5cf6" fontSize="11" fontWeight="600">
        Новый коммит
      </text>

      <line x1="400" y1="80" x2="520" y2="80" stroke="#f97316" strokeWidth="2" />
      <circle cx="420" cy="80" r="5" fill="#f97316" />
      <circle cx="450" cy="80" r="5" fill="#f97316" />
      <circle cx="480" cy="80" r="5" fill="#f97316" />
      <circle cx="510" cy="80" r="6" fill="#8b5cf6" stroke="#7c3aed" strokeWidth="2" />
      <text x="390" y="85" fill="#f97316" fontSize="8">
        main
      </text>

      {/* HEAD pointer moved */}
      <rect x="495" y="95" width="40" height="20" rx="3" fill="#8b5cf6" />
      <text x="515" y="109" textAnchor="middle" fill="white" fontSize="8" fontWeight="600">
        HEAD
      </text>
      <path d="M515 95 L515 86" stroke="#8b5cf6" strokeWidth="2" />

      <text x="460" y="130" textAnchor="middle" fill="#94a3b8" fontSize="8">
        git commit → HEAD движется
      </text>

      {/* Commands reference */}
      <rect x="20" y="160" width="520" height="100" rx="8" fill="#0f172a" stroke="#475569" />
      <text x="280" y="180" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="600">
        Команды для перемещения HEAD
      </text>

      <text x="40" y="205" fill="#22c55e" fontSize="9">
        git checkout branch
      </text>
      <text x="170" y="205" fill="#94a3b8" fontSize="9">
        — HEAD на ветку
      </text>

      <text x="40" y="225" fill="#ef4444" fontSize="9">
        git checkout sha
      </text>
      <text x="155" y="225" fill="#94a3b8" fontSize="9">
        — detached HEAD
      </text>

      <text x="40" y="245" fill="#8b5cf6" fontSize="9">
        git checkout HEAD~1
      </text>
      <text x="175" y="245" fill="#94a3b8" fontSize="9">
        — на 1 коммит назад
      </text>

      <text x="300" y="205" fill="#06b6d4" fontSize="9">
        git switch branch
      </text>
      <text x="415" y="205" fill="#94a3b8" fontSize="9">
        — новый способ
      </text>

      <text x="300" y="225" fill="#eab308" fontSize="9">
        git reset --hard sha
      </text>
      <text x="430" y="225" fill="#94a3b8" fontSize="9">
        — переместить ветку
      </text>

      <text x="300" y="245" fill="#22c55e" fontSize="9">
        git checkout -
      </text>
      <text x="390" y="245" fill="#94a3b8" fontSize="9">
        — предыдущая ветка
      </text>
    </svg>
  );
}

export default Diagram;
